import { bytesToEncoded } from '../byteUtils.js';
import { createInlineKeyPair } from '@amzn/piezo-plenum-typescript';
import { ValidationException } from '@amzn/piezo-plenum-types';
import { PiezoDaemonClient, StartActionCommand, UpdateTrustRootsCommand, GetHealthCommand } from '@amzn/piezo-daemon-typescript-client';
/**
 * Enum representing whether the request will include additional input to pass to the action (as STDIN input).
 */
export var InputMode;
(function (InputMode) {
    InputMode["None"] = "NONE";
    InputMode["Appended"] = "APPENDED";
})(InputMode || (InputMode = {}));
export class PiezoDaemonThickClient {
    plenumClient;
    daemonClient;
    constructor(plenumClient, endpoint) {
        this.plenumClient = plenumClient;
        this.daemonClient = new PiezoDaemonClient({ endpoint });
    }
    /**
     * Gets the health status of the daemon.
     *
     * @returns The health response from the daemon
     */
    async getHealth() {
        const command = new GetHealthCommand({});
        return await this.daemonClient.send(command);
    }
    /**
     * Takes key information, an actionInfo object, and optionally roles and creates a plenum document encapsulating
     * the actionInfo, signed by the provided key information.
     * @deprecated Use PiezoPlenumClient.createActionDocuments() instead
     */
    async encodeActionInfoToPlenum(keyProvider, keyId, actionInfo, roles = []) {
        const dataToSerialize = { version: '1.0', ...actionInfo };
        const data = window.btoa(JSON.stringify(dataToSerialize));
        const encodedDoc = await this.plenumClient.createPlenumDocument({
            keyId: bytesToEncoded(keyId),
            roles,
            keypair: keyProvider.getPrivateCryptoKey() ? createInlineKeyPair() : await keyProvider.getPlenumKeyPair(),
            data,
        }, keyProvider.getPrivateCryptoKey());
        return this.plenumClient.decodePlenumDocument(encodedDoc.document);
    }
    /**
     * Executes an action against the given daemon with the given input.
     *
     * @argument input: The actionInfo, decks, permits, and keyProvider required to execute the action.
     * @returns the StartActionOutput from the daemon with the STDOUT, STDERR, and exit code.
     */
    async executeAction(input) {
        // Create the payload with create piezo frame
        const payload = await this.createPiezoFrames(input.requesterDecks, input.additionalDecks, input.permits, input.observationDocuments || [], input.actionInfo, input.keyProvider, input.actionInputDocuments);
        // Execute the action
        const command = new StartActionCommand({
            request: payload
        });
        const response = await this.daemonClient.send(command);
        return {
            stdout: response.stdout || new Uint8Array(),
            stderr: response.stderr || new Uint8Array(),
            exitCode: response.exitCode || "1"
        };
    }
    /**
     * Concatenates multiple issuer trust roots into a single byte array.
     *
     * @param trustRoots List of Uint8Array containing trust roots to concatenate
     * @returns Concatenated trust roots as a single Uint8Array
     * @throws Error if the input list is empty
     */
    concatenateIssuerTrustRoots(trustRoots) {
        if (!trustRoots || trustRoots.length === 0) {
            throw new Error("Cannot concatenate empty or null trust roots list");
        }
        // Calculate total size
        const totalSize = trustRoots.reduce((sum, doc) => sum + doc.length, 0);
        // Concatenate all documents
        const result = new Uint8Array(totalSize);
        let offset = 0;
        for (const doc of trustRoots) {
            result.set(doc, offset);
            offset += doc.length;
        }
        return result;
    }
    /**
     * Updates the trust roots in the daemon.
     *
     * @param trustRoots Array of trust roots to update
     * @returns Response from the updateTrustRoots API call
     * @throws Error if the update fails
     */
    async updateTrustRoots(trustRoots) {
        try {
            // Concatenate the trust roots
            const concatenatedDocuments = this.concatenateIssuerTrustRoots(trustRoots);
            // Execute the updateTrustRoots command
            console.log(`Updating trust roots`);
            const command = new UpdateTrustRootsCommand({
                documents: concatenatedDocuments
            });
            // Send the command
            await this.daemonClient.send(command);
        }
        catch (error) {
            console.error('Error updating trust roots:', error);
            throw error;
        }
    }
    /**
     * Parses an ActionInfo plenum document and returns ActionInfo with plenum roles.
     *
     * @param actionInfoBytes The raw bytes of the action info plenum document
     * @returns ActionInfo combined with author and approver roles
     * @throws ValidationException if plenum is invalid
     * @deprecated Use PiezoPlenumClient.parsePlenumDocument() instead
     */
    async parseActionInfo(actionInfoBytes) {
        const encodedDocument = bytesToEncoded(actionInfoBytes);
        const parseResult = await this.plenumClient.parsePlenumDocument({
            document: encodedDocument
        });
        if (!parseResult.data) {
            throw new ValidationException({
                message: 'Plenum document contains no data',
                $metadata: {}
            });
        }
        if (!parseResult.authorKeyId) {
            throw new ValidationException({
                message: 'Plenum document missing author key ID',
                $metadata: {}
            });
        }
        const actionInfoData = JSON.parse(atob(parseResult.data));
        if (!actionInfoData.time || !actionInfoData.serverIdentifiers || !actionInfoData.action ||
            !actionInfoData.action.name || !actionInfoData.action.arguments ||
            !Object.values(InputMode).includes(actionInfoData.action.inputMode)) {
            throw new ValidationException({
                message: 'Failed to convert to ActionInfo: invalid structure',
                $metadata: {}
            });
        }
        return {
            ...actionInfoData,
            author: {
                keyId: parseResult.authorKeyId,
                roles: parseResult.authorRoles ?? []
            },
            approvers: parseResult.signers ?? []
        };
    }
    /**
     * Creates a properly formatted Piezo frame for the daemon request.
     */
    async createPiezoFrames(requesterDecks, additionalDecks, permits, observationDocuments, actionInfo, keyProvider, actionInputDocuments) {
        // Set protocol version to 2 when observation documents are present
        const piezoProtocolVersion = observationDocuments.length > 0 ? 2 : undefined;
        const requestOutput = await this.plenumClient.createPiezoRequest({
            keypair: keyProvider.getPrivateCryptoKey() ? createInlineKeyPair() : await keyProvider.getPlenumKeyPair(),
            requesterDecks: requesterDecks.map(deck => bytesToEncoded(deck)),
            additionalDecks: additionalDecks.map(deck => bytesToEncoded(deck)),
            permits: permits.map(permit => bytesToEncoded(permit.permit)),
            observationDocuments: observationDocuments.map(doc => bytesToEncoded(doc)),
            actionInfo: bytesToEncoded(actionInfo),
            actionInputs: actionInputDocuments?.map(doc => bytesToEncoded(doc)),
            piezoProtocolVersion,
        }, keyProvider.getPrivateCryptoKey());
        return this.plenumClient.decodePlenumDocument(requestOutput.piezoRequest);
    }
}
